// Images resolve against the website origin: fullSizeResizeUrl is a
// root-relative /resizer/... path.
const IMAGE_BASE_URL = 'https://www.irishtimes.com';

function resolveImage(promoImage) {
  const url = promoImage?.additional_properties?.fullSizeResizeUrl || promoImage?.url || '';
  return url.startsWith('/') ? `${IMAGE_BASE_URL}${url}` : url;
}

// Picks the best video stream from a lead-art video's streams array.
// Favour HLS (m3u8) for adaptive playback; fall back to the highest-bitrate
// mp4. Returns null when there are no usable streams.
function pickVideoStream(leadArt) {
  const streams = leadArt?.streams;
  if (!Array.isArray(streams) || streams.length === 0) return null;

  const hls = streams
    .filter((s) => s.stream_type === 'ts' && s.url?.endsWith('.m3u8'))
    .sort((a, b) => (b.bitrate || 0) - (a.bitrate || 0));
  if (hls.length > 0) return hls[0].url;

  const mp4 = streams
    .filter((s) => s.stream_type === 'mp4' && s.url)
    .sort((a, b) => (b.bitrate || 0) - (a.bitrate || 0));
  return mp4.length > 0 ? mp4[0].url : null;
}

// Resolves the lead art: when it's a video, returns the stream URL and the
// poster image (the lead art's own image, or the basic promo image). When
// it's an image, or there's no lead art, returns the image as before.
function resolveLeadArt(story) {
  const leadArt = story.promo_items?.lead_art;
  const leadArtImage = resolveImage(leadArt);

  if (leadArt?.type === 'video') {
    const stream = pickVideoStream(leadArt);
    if (stream) {
      return {
        image: leadArtImage || resolveImage(story.promo_items?.basic),
        videoUrl: stream,
      };
    }
  }

  return { image: leadArtImage || resolveImage(story.promo_items?.basic), videoUrl: '' };
}

// Normalises an Arc story-feed item into the flat shape the story cards
// render. Field paths follow the story-feed-query response format.
export function mapStory(story) {
  const author = story.credits?.by?.[0];
  const wordCount = story.planning?.story_length?.word_count_actual;
  const { image, videoUrl } = resolveLeadArt(story);

  return {
    id: story._id,
    subtype: story.subtype || '',
    headline: story.headlines?.basic || 'Untitled story',
    standfirst: story.subheadlines?.basic || story.description?.basic || '',
    author: author?.name || '',
    authorImage: author?.image?.url || '',
    image,
    videoUrl,
    audioUrl: story.promo_items?.podcast?.embed?.config?.streamUrl || '',
    articleAudioUrl: story.additional_properties?.audio_url || '',
    section: story.taxonomy?.primary_section?.name || '',
    publishedAt: story.display_date || story.publish_date || '',
    readMinutes: wordCount ? Math.max(1, Math.round(wordCount / 250)) : null,
  };
}

// Newest first. Stories with no date sink to the bottom rather than
// scrambling the order around them.
export function byNewestFirst(stories) {
  const time = (story) =>
    story.publishedAt ? new Date(story.publishedAt).getTime() : -Infinity;

  return stories.slice().sort((a, b) => time(b) - time(a));
}

export function timeAgo(isoDate) {
  if (!isoDate) return '';

  const mins = Math.round((Date.now() - new Date(isoDate).getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return mins === 1 ? '1 min ago' : `${mins} mins ago`;

  const hours = Math.round(mins / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;

  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}
